import { useState, type FormEvent } from 'react';
import {
  EmailAuthProvider,
  reauthenticateWithCredential,
  TotpMultiFactorGenerator,
  multiFactor,
  getMultiFactorResolver,
  type MultiFactorResolver,
} from 'firebase/auth';
import { darkTheme } from '../../lib/tokens';
import { getFirebaseAuth } from '../../lib/firebase';
import { getFreshIdToken } from './firebaseAuth';
import { api } from '../../lib/api';
import { useAuthStore } from './authStore';

// Step-up-Modal für destruktive Admin-Aktionen.
// Wird aufgerufen wie:
//   const stepUp = useStepUp();
//   await stepUp.request({
//     action: 'user.hardDelete',
//     description: 'User user@example.com endgültig löschen',
//   });
//   // → liefert Step-up-Token zurück; mit dem ruft der Aufrufer den
//   //   destruktiven Endpoint auf (X-Step-Up-Token-Header).

export type StepUpAction =
  | 'user.suspend'
  | 'user.unsuspend'
  | 'user.softDelete'
  | 'user.hardDelete'
  | 'user.subscriptionOverride'
  | 'user.addonOverride'
  | 'user.quotaGrant'
  | 'user.bulkAction'
  | 'broadcast.sendAll'
  | 'service.restart'
  | 'cron.run'
  | 'cache.invalidate';

export interface StepUpRequest {
  action: StepUpAction;
  description: string;
  destructive?: boolean;
}

interface StepUpModalProps {
  request: StepUpRequest;
  onConfirm: (token: string) => void | Promise<void>;
  onCancel: () => void;
}

export function StepUpModal({ request, onConfirm, onCancel }: StepUpModalProps): JSX.Element {
  const email = useAuthStore((s) => s.user?.email ?? '');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const token = await reauthenticateAndIssueStepUpToken({
        email,
        password,
        totpCode: code,
        action: request.action,
      });
      await onConfirm(token);
    } catch (err) {
      setError(humanError(err));
      setCode('');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-[400px] rounded-2xl border p-6 shadow-2xl"
        style={{
          background: darkTheme.colors.bgSurface,
          borderColor: darkTheme.colors.borderSubtle,
        }}
      >
        <div className="mb-4 flex items-center gap-2">
          <span
            className="flex h-7 w-7 items-center justify-center rounded-full text-xs"
            style={{ background: darkTheme.colors.accentSubtle, color: darkTheme.colors.accent }}
            aria-hidden
          >
            🔒
          </span>
          <h2 className="text-base font-semibold text-text-primary">Bestätigung erforderlich</h2>
        </div>

        <p className="mb-5 text-sm text-text-secondary">
          Du bist dabei: <span className="font-semibold text-text-primary">{request.description}</span>
          . Bitte bestätige mit Passwort und Authenticator-Code.
        </p>

        <div className="space-y-3">
          <Field label="Passwort">
            <input
              type="password"
              required
              autoFocus
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-md border bg-bg-base px-3 py-2 text-sm text-text-primary outline-none focus:border-accent"
              style={{ borderColor: darkTheme.colors.borderSubtle }}
            />
          </Field>

          <Field label="Authenticator-Code">
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]{6}"
              maxLength={6}
              required
              autoComplete="one-time-code"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              className="w-full rounded-md border bg-bg-base px-3 py-2 text-center text-base tabular tracking-[0.4em] text-text-primary outline-none focus:border-accent"
              style={{ borderColor: darkTheme.colors.borderSubtle }}
            />
          </Field>
        </div>

        {error && <p className="mt-3 text-sm text-danger">{error}</p>}

        <div className="mt-5 flex items-center gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 rounded-md px-3 py-2 text-sm text-text-secondary hover:bg-bg-elevated"
          >
            Abbrechen
          </button>
          <button
            type="submit"
            disabled={submitting || code.length !== 6 || !password}
            className="flex-1 rounded-md px-3 py-2 text-sm font-semibold text-white transition disabled:opacity-50"
            style={{
              background: request.destructive ? darkTheme.colors.danger : darkTheme.colors.accent,
            }}
          >
            {submitting ? 'Wird geprüft …' : 'Bestätigen'}
          </button>
        </div>

        <p className="mt-3 text-center text-[11px] text-text-tertiary">
          Diese Bestätigung ist 5 Minuten gültig.
        </p>
      </form>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium text-text-secondary">{label}</span>
      {children}
    </label>
  );
}

// Re-Auth + Step-up-Token-Issue. Re-Auth verifiziert Passwort, dann TOTP, dann
// holen wir einen frischen ID-Token mit den Claims (auth_time + secondFactor)
// und tauschen ihn gegen ein Step-up-Token vom Backend.
async function reauthenticateAndIssueStepUpToken(params: {
  email: string;
  password: string;
  totpCode: string;
  action: StepUpAction;
}): Promise<string> {
  const auth = getFirebaseAuth();
  const user = auth.currentUser;
  if (!user) {
    throw new Error('Nicht angemeldet — bitte erneut einloggen.');
  }

  // Schritt 1: Passwort verifizieren via reauthenticateWithCredential.
  // Wenn der User MFA aktiviert hat, wirft Firebase auth/multi-factor-auth-required
  // — wir fangen das ab und resolven mit dem TOTP-Code.
  const credential = EmailAuthProvider.credential(params.email, params.password);
  let resolver: MultiFactorResolver | null = null;

  try {
    await reauthenticateWithCredential(user, credential);
  } catch (err) {
    const code = (err as { code?: string }).code;
    if (code === 'auth/multi-factor-auth-required') {
      resolver = getMultiFactorResolver(auth, err as never);
    } else {
      throw err;
    }
  }

  // Schritt 2: TOTP-Challenge resolven (falls nötig — bei Admins immer).
  if (resolver) {
    const totpFactor = resolver.hints.find((h) => h.factorId === TotpMultiFactorGenerator.FACTOR_ID);
    if (!totpFactor) {
      throw new Error('Kein TOTP-Faktor konfiguriert.');
    }
    const assertion = TotpMultiFactorGenerator.assertionForSignIn(totpFactor.uid, params.totpCode);
    await resolver.resolveSignIn(assertion);
  } else {
    // User hatte kein MFA aktiviert (sollte für Admins nicht passieren).
    // Wir prüfen das in einem zweiten Schritt: enrolledFactors muss TOTP enthalten,
    // sonst wird der Backend-Step-up-Endpoint mit 403 abblocken.
    const factors = multiFactor(user).enrolledFactors;
    const hasTotp = factors.some((f) => f.factorId === TotpMultiFactorGenerator.FACTOR_ID);
    if (!hasTotp) {
      throw new Error('Kein zweiter Faktor aktiviert. Aktion verweigert.');
    }
    // Edge-Case: User hat TOTP enrolled, aber Firebase hat keinen MFA-Resolver
    // erzwungen (z.B. weil die Session frisch genug ist). Wir akzeptieren das
    // nicht — Backend prüft auth_time und Faktor strikt.
  }

  // Schritt 3: frischen ID-Token holen (mit aktualisiertem auth_time + sign_in_second_factor).
  const idToken = await getFreshIdToken();

  // Schritt 4: Step-up-Token vom Backend anfordern.
  const result = await api.post<{ token: string; expiresInSeconds: number }>(
    '/admin/auth/step-up',
    {
      firebaseToken: idToken,
      action: params.action,
    },
  );

  return result.token;
}

function humanError(err: unknown): string {
  const code = (err as { code?: string }).code;
  switch (code) {
    case 'auth/invalid-credential':
    case 'auth/wrong-password':
      return 'Passwort falsch.';
    case 'auth/invalid-verification-code':
      return 'Authenticator-Code ungültig.';
    case 'auth/too-many-requests':
      return 'Zu viele Versuche — bitte kurz warten.';
    default: {
      const message = (err as { message?: string }).message;
      return message ?? 'Bestätigung fehlgeschlagen.';
    }
  }
}
